import { TreeNode, ContextMenuAction } from '../../explorer/models/TreeModel';
import { generateRoutineContextMenuActions } from './routineContextMenuService';
import {
  isSecurityObjectType,
  generateSecurityObjectContextMenuActions,
} from './securityContextMenuService';
import { generateOracleObjectContextMenuActions } from './oracleObjectContextMenuService';
import {
  isUserTypeObjectType,
  generateUserTypeContextMenuActions,
} from './userTypeContextMenuService';
import { createRefreshAction } from '../../explorer/services/contextMenuService';

const ORACLE_OBJECT_TYPES = ['package', 'sequence', 'synonym', 'db-link', 'materialized-view', 'type', 'property-graph'] as const;

function isOracleObjectType(type: string): boolean {
  return (ORACLE_OBJECT_TYPES as readonly string[]).includes(type);
}

/**
 * Process tree nodes to add context menu actions based on node type
 * 
 * This function should be called before displaying the tree to ensure
 * all database object nodes have appropriate context menu actions.
 */
export function processDatabaseTree(treeData: TreeNode[]): TreeNode[] {
  return treeData.map(node => processNode(node));
}

/**
 * Process a single node to add context menu actions
 */
function processNode(node: TreeNode): TreeNode {
  // Add type-specific context menu actions based on node type
  const processed = { ...node };
  let contextMenuActions: ContextMenuAction[] = [];
  
  // Table/view menus are intentionally NOT built here (`contextMenuActions`
  // stays `[]` for these two types below). `TreeNode.tsx` always recomputes
  // both live at render time — with the engine + AI-CLI-gating args this
  // tree-build pass doesn't have — before either type reaches the DOM
  // (`effectiveType === 'table' | 'view'` branches, unconditional for every
  // table/view row), so anything built here is discarded. Building it anyway
  // used to leave an *ungated* copy of the "AI" submenu (always enabled,
  // engine-unaware) sitting in the tree — dead today, but the kind of residue
  // a future caller could accidentally wire up (T12 fix round 1, M-3).
  if (node.type === 'table' || node.type === 'view') {
    // intentionally left as [] — see comment above.
  }
  // Add routine-specific context menu actions
  else if (node.type === 'stored-procedure' || node.type === 'function') {
    contextMenuActions = generateRoutineContextMenuActions(node.type);
  }
  // SQL Server user-defined types (Programmability -> Types)
  else if (isUserTypeObjectType(node.type)) {
    contextMenuActions = generateUserTypeContextMenuActions(node.type);
  }
  // Add Oracle-specific object context menu actions
  else if (isOracleObjectType(node.type)) {
    contextMenuActions = generateOracleObjectContextMenuActions(node.type as any);
  }
  // Add security object context menu actions
  else if (isSecurityObjectType(node.type)) {
    contextMenuActions = generateSecurityObjectContextMenuActions(node.type);
  }
  // All nodes should have at least the refresh action if they can have children
  // or already have children
  else if (node.canHaveChildren || 
          (node.children && node.children.length > 0) || 
          node.type.includes('folder') || 
          node.type === 'database' || 
          node.type === 'connection') {
    contextMenuActions = [createRefreshAction()];
  }
  
  // Always add context menu actions to the node, even if it's an empty array
  // This ensures the hasContextMenu check will work properly in TreeNode.tsx
  processed.contextMenuActions = contextMenuActions;
  
  // Process children recursively
  if (node.children && node.children.length > 0) {
    processed.children = node.children.map(child => processNode(child));
  }
  
  return processed;
}
